import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Logo } from '@/ui'
import { userSelectors } from '@/entity/user'
import { Logout } from '@features/auth'
import styles from './Sidebar.module.scss'
import { formsActions } from '@/entity/forms'
import { companySelectors } from '@/entity/company'
import { authSelectors } from '@app/store/auth'
import { Loader } from '@/components/Loader'
import { employerProfileSelectors } from '@features/employerProfile/model'
import React, { useCallback } from 'react'
import { CollapsibleMenu } from '@/widgets/sidebar/CollapsibleMenu'
import {
	EmploymentContractIcon,
	LaborProtectionIcon,
	LaborRecordsIcon,
	LogoutIcon,
	NotificationsIcon,
	OnlineConsultantIcon,
	PersonalAffairsIcon,
	PersonalRecordsIcon,
	GradingIcon,
	ProfileIcon,
	ProfUnions,
	StaffingIcon,
	ReviewsIcon,
	AdministrationIcon,
	SickLeaveIcon,
	TimingWorkIcon,
	NominationIcon,
} from '@/widgets/sidebar/Sidebar.icons'
import { nominationSelectors } from '@/entity/nomination'
import { getAllowedBins } from '@/config/binsConfig'

const SidebarComponent = () => {
	const dispatch = useDispatch()
	const { t } = useTranslation()

	// Берём только примитивы из стора, чтобы не триггерить перерисовку по смене ссылок на объекты
	const {
		activeRole,
		companiesLength,
		hasUser,
		verifyPerson,
		isLoading,
		activeCompany,
		flEmployer,
		industryAgreement,
		mio,
		dtsp,
		bin,
	} = useSelector(
		(state) => ({
			activeRole: userSelectors.activeRole(state),
			companiesLength: companySelectors.companies(state)?.length || 0,
			hasUser: !!userSelectors.user(state),
			verifyPerson: !!userSelectors.user(state)?.iin,
			isLoading: authSelectors.isLoading(state),
			flEmployer: employerProfileSelectors.activeCompany(state)?.flEmployer,
			industryAgreement: companySelectors.company(state)?.industryAgreement,
			mio: nominationSelectors.mioUserCheck(state)?.data?.mio,
			dtsp: nominationSelectors.mioUserCheck(state)?.data?.dtsp,
			bin: employerProfileSelectors.activeCompany(state)?.bin,
		}),
		shallowEqual
	)

	const isEmployee = activeRole === 'ROLE_EMPLOYEE'
	const isEmployer = activeRole === 'ROLE_EMPLOYER'

	const clearForm = useCallback(() => {
		dispatch(formsActions.themesLoading([]))
		dispatch(formsActions.questionsLoading([]))
		dispatch(formsActions.selfTestResultResponse([]))
		dispatch(formsActions.answerSelfTestLoading([]))
	}, [dispatch])

	const isVisibleOpr = React.useMemo(() => {
		if (process.env.NODE_ENV === 'development') return true

		const allowedBins = getAllowedBins()
		return allowedBins.includes(activeCompany?.bin)
	}, [activeCompany?.bin])

	return (
		<div className={styles.sidebar} style={{ width: 245, minHeight: '100vh', flex: '0 0 245px' }}>
			<NavLink to="/" className={({ isActive }) => `${styles.logo} ${isActive ? styles.navLinkActive : ''}`}>
				<span className={styles.logo}>
					<Logo />
				</span>
			</NavLink>

			{isLoading && (
				<div className={styles.loading}>
					<Loader />
				</div>
			)}

			{!hasUser && !isLoading && (
				<nav className={styles.navigation}>
					<NavLink
						onClick={clearForm}
						to={'/forms'}
						className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
					>
						<OnlineConsultantIcon />
						<p>{t('online_consultant')}</p>
					</NavLink>
				</nav>
			)}
			{hasUser && !isLoading && (
				<nav className={styles.navigation}>
					<NavLink
						to={isEmployee ? '/worker' : '/employer'}
						className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
					>
						<div className={`${styles.item__title}`}>
							<ProfileIcon />
							<p>{t('profile')}</p>
						</div>
					</NavLink>
					<CollapsibleMenu className={styles.item} title="employment_contract" icon={<EmploymentContractIcon />}>
						<NavLink
							to="/contracts"
							className={({ isActive }) =>
								`${styles.item} ${
									(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
								} ${isActive ? styles.navLinkActive : ''}`
							}
						>
							<p>{t('employment_contract')}</p>
						</NavLink>
						{isEmployer ? (
							<NavLink
								to="/сollective-сontracts"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('collective_contract')}</p>
							</NavLink>
						) : null}
						{isEmployer && (
							<NavLink
								to="/contracts-civil-low"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength && isEmployer ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('contracts_civil_low.title')}</p>
							</NavLink>
						)}
					</CollapsibleMenu>
					{isEmployer && (
						<NavLink
							to="/staffing"
							className={({ isActive }) =>
								`${styles.item} ${(!companiesLength && isEmployer) || flEmployer ? styles.item__disabled : ''} ${
									isActive ? styles.navLinkActive : ''
								}`
							}
						>
							<StaffingIcon />
							<p>{t('staffing')}</p>
						</NavLink>
					)}
					{isEmployee ? (
						<>
							<NavLink
								to="/labor-records"
								className={({ isActive }) =>
									`${styles.item} ${!verifyPerson ? styles.item__disabled : ''} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<LaborRecordsIcon />
								<p>{t('labor_records.title')}</p>
							</NavLink>
							<NavLink
								to="/employees-personal-files"
								className={({ isActive }) =>
									`${styles.item} ${!verifyPerson ? styles.item__disabled : ''} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<PersonalRecordsIcon />
								<p>{t('personal_records')}</p>
							</NavLink>
							<NavLink
								to="/timing-work"
								className={({ isActive }) =>
									`${styles.item} ${!verifyPerson ? styles.item__disabled : ''} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<TimingWorkIcon />
								<p>{t('timing_work')}</p>
							</NavLink>

							<NavLink
								to="/sick-leave"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<SickLeaveIcon />
								<p>{t('sickLeave.title')}</p>
							</NavLink>
						</>
					) : (
						<CollapsibleMenu className={styles.item} title="personal_affairs.worker" icon={<PersonalAffairsIcon />}>
							<NavLink
								to="/personal-affairs"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) || flEmployer
											? styles.item__disabled
											: ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('personal_records')}</p>
							</NavLink>

							<NavLink
								to="/payment"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) || flEmployer
											? styles.item__disabled
											: ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('payment.tab')}</p>
							</NavLink>

							<NavLink
								to="/sick-leave"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('sickLeave.title')}</p>
							</NavLink>

							{!flEmployer && (
								<NavLink
									to="/irs-accepted"
									className={({ isActive }) =>
										`${styles.item} ${
											(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
										} ${isActive ? styles.navLinkActive : ''}`
									}
								>
									<p>{t('ckp_IRS_permissions')}</p>
								</NavLink>
							)}

							<NavLink
								to="/timing-work"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) || flEmployer
											? styles.item__disabled
											: ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('timing_work')}</p>
							</NavLink>

							<NavLink
								to="/mismatch"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('mismatch.title')}</p>
							</NavLink>
						</CollapsibleMenu>
					)}

					<NavLink
						to="/reviews"
						className={({ isActive }) =>
							`${styles.item} ${
								(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
							} ${isActive ? styles.navLinkActive : ''}`
						}
					>
						<ReviewsIcon />
						<p>{t('reviews.title')}</p>
					</NavLink>

					{!flEmployer ? (
						<CollapsibleMenu className={styles.item} title="profUnions.name" icon={<ProfUnions />}>
							<NavLink
								to="/prof"
								className={({ isActive }) =>
									`${styles.item} ${
										(isEmployee && !verifyPerson) || (!companiesLength && isEmployer) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('profUnions.generalInformation')}</p>
							</NavLink>
							<NavLink
								to="/trade-members"
								className={({ isActive }) =>
									`${styles.item} ${
										(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('profUnions.trade_members')}</p>
							</NavLink>
							{isEmployer ? (
								<>
									<NavLink
										to="/trade-unions"
										className={({ isActive }) =>
											`${styles.item} ${!companiesLength && isEmployer ? styles.item__disabled : ''} ${
												isActive ? styles.navLinkActive : ''
											}`
										}
									>
										<p>{t('profUnions.tradeUnions')}</p>
									</NavLink>
								</>
							) : (
								<NavLink
									to="/my-unions"
									className={({ isActive }) =>
										`${styles.item} ${
											(isEmployee && !verifyPerson) || (!companiesLength && isEmployer) ? styles.item__disabled : ''
										} ${isActive ? styles.navLinkActive : ''}`
									}
								>
									<p>{t('profUnions.employee_trade_unions')}</p>
								</NavLink>
							)}
							<NavLink
								to="/col-contracts"
								className={({ isActive }) =>
									`${styles.item} ${
										(isEmployee && !verifyPerson) || (!companiesLength && isEmployer) ? styles.item__disabled : ''
									} ${isActive ? styles.navLinkActive : ''}`
								}
							>
								<p>{t('col_contract.contracts')}</p>
							</NavLink>
							{isEmployer && industryAgreement ? (
								<NavLink
									to="/industry-agreement"
									className={({ isActive }) =>
										`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
											isActive ? styles.navLinkActive : ''
										}`
									}
								>
									<p>{t('industryAgreement.title')}</p>
								</NavLink>
							) : null}
						</CollapsibleMenu>
					) : null}
					{isEmployer ? (
						<CollapsibleMenu className={styles.item} title="laborProtection" icon={<LaborProtectionIcon />}>
							<NavLink
								to="/company-passport"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('ckp_enterprise_passport')}</p>
							</NavLink>
							<NavLink
								to="/employer-passport"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('ckp_workers_portrait')}</p>
							</NavLink>
							<NavLink
								to="/accidents"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('accidents')}</p>
							</NavLink>
							<NavLink
								to="/checks"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('ckp_checks')}</p>
							</NavLink>
							{isVisibleOpr && (
								<CollapsibleMenu className={styles.item} title="ckp_occupational_risks_assessment">
									<NavLink
										to="/applications-ora"
										className={({ isActive }) =>
											`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
												isActive ? styles.navLinkActive : ''
											}`
										}
									>
										<p>{t('occupational_risks_assessment.applications.title')}</p>
									</NavLink>
									<NavLink
										to="/regulatory-information"
										className={({ isActive }) =>
											`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
												isActive ? styles.navLinkActive : ''
											}`
										}
									>
										<p>{t('occupational_risks_assessment.regulatory_information.title')}</p>
									</NavLink>
								</CollapsibleMenu>
							)}
							<NavLink
								to="/requirement-labor-resources"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('requirement_labor_resources.title')}</p>
							</NavLink>
							<NavLink
								to="/violations"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<p>{t('ckp_violations_monitoring')}</p>
							</NavLink>
						</CollapsibleMenu>
					) : (
						<CollapsibleMenu className={styles.item} title="laborProtection" icon={<LaborProtectionIcon />}>
							{' '}
							<NavLink
								to="/accidents"
								className={({ isActive }) =>
								`${styles.item} ${isEmployee && !verifyPerson ? styles.item__disabled : ''} ${
									isActive ? styles.navLinkActive : ''
										}`
							}
							>
								<p>{t('accidents')}</p>
							</NavLink>
						</CollapsibleMenu>
					)}
					{/*	//TODO временно комментируется 12.09.2024
						<NavLink to="/grading" className={styles.item} activeClassName={styles.navLinkActive}>
						<GradingIcon />
						<p>{t('grading')}</p>
					</NavLink>*/}
					<NavLink
						to="/notifications"
						className={({ isActive }) =>
							`${styles.item} ${
								(!companiesLength && isEmployer) || (isEmployee && !verifyPerson) ? styles.item__disabled : ''
							} ${isActive ? styles.navLinkActive : ''}`
						}
					>
						<NotificationsIcon />
						<p>{t('notifications')}</p>
					</NavLink>

					{isEmployer ? (
						<>
							<NavLink
								to="/employer-nomination"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<NominationIcon />
								<p>{t('nomination.title')}</p>
							</NavLink>
							<NavLink
								onClick={clearForm}
								to={'/forms'}
								className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
							>
								<OnlineConsultantIcon />
								<p>{t('online_consultant')}</p>
							</NavLink>

							<NavLink
								to="/administration"
								className={({ isActive }) =>
									`${styles.item} ${!companiesLength ? styles.item__disabled : ''} ${
										isActive ? styles.navLinkActive : ''
									}`
								}
							>
								<AdministrationIcon />
								<p>{t('administration.title')}</p>
							</NavLink>
						</>
					) : (
						<CollapsibleMenu className={styles.item} title="nomination.title" icon={<NominationIcon />}>
							{mio && (
								<>
									<NavLink
										to="/mio-nomination-request"
										className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
									>
										<p>
											{t('nomination.request')} {mio && dtsp && '(МИО)'}
										</p>
									</NavLink>
									<NavLink
										to="/mio-nomination"
										className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
									>
										<p>
											{t('nomination.nominations')} {mio && dtsp && '(МИО)'}
										</p>
									</NavLink>
								</>
							)}

							{dtsp && (
								<>
									<NavLink
										to="/dtsp-nomination-request"
										className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
									>
										<p>
											{t('nomination.request')} {mio && dtsp && '(ДТСП)'}
										</p>
									</NavLink>
									<NavLink
										to="/dtsp-nomination"
										className={({ isActive }) => `${styles.item} ${isActive ? styles.navLinkActive : ''}`}
									>
										<p>
											{t('nomination.nominations')} {mio && dtsp && '(ДТСП)'}
										</p>
									</NavLink>
								</>
							)}
						</CollapsibleMenu>
					)}
					<Logout>
						<span className={styles.spanLogout}>
							<LogoutIcon />
							<p>{t('exit')}</p>
						</span>
					</Logout>
				</nav>
			)}
		</div>
	)
}

export const Sidebar = React.memo(SidebarComponent)
